import { ClbRadioButton, ClbRadioGroup } from "@celebration/design-system-react";
import Button from "components/button";
import Loading from "components/center-loading";
import Feature from "components/feature";
import { SelectBox } from "components/uikit-adapter/index";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import intl from "react-intl-universal";
import {
  getAllLabeledFamilies,
  getBusinessUnitByCountries,
  getCountriesForBusinessUnits,
  getMaterialGroups,
} from "../../data-upload.service";
import * as S from "../filter-data-upload/styled";

const loadFilterOptions = async () => {
  const defaultFilter = { useFilterDefault: JSON.parse(localStorage.getItem("filterMyItemsOnly")) };
  const [labeledFamily, countries, materialGroup, center] = await Promise.all([
    getAllLabeledFamilies(),
    getCountriesForBusinessUnits(),
    getMaterialGroups(defaultFilter),
    getBusinessUnitByCountries([]),
  ]);
  return { labeledFamily, countries, materialGroup, center };
};

const FilterDataUploadV2 = ({ onClick }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [activeMaterial, setActiveMaterial] = useState("activeMaterial");

  const [options, setOptions] = useState({
    materialGroup: [],
    labeledFamily: [],
    center: [],
    countries: [],
  });

  const [data, setData] = useState({
    materialGroup: [],
    labeledFamily: [],
    center: [],
    countries: [],
  });

  const handleData = (info, name) => {
    setData((prevState) => ({ ...prevState, [name]: info }));
  };

  const handleActiveMaterial = (e) => {
    setActiveMaterial(e.detail.value);
  };

  useEffect(() => {
    loadFilterOptions().then(setOptions);
  }, []);

  useEffect(() => {
    getBusinessUnitByCountries(data.countries).then((center) =>
      setOptions((prev) => ({ ...prev, center }))
    );
  }, [data.countries]);

  const handleMultiPaste = (e, currentValues, fieldOptions, labelKey, onChange) => {
    const pastedText = e.clipboardData.getData("text");
    const lines = pastedText
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    if (lines.length <= 1) return;
    e.preventDefault();
    const matched = fieldOptions.filter((opt) =>
      lines.some((line) => opt[labelKey].toLowerCase() === line.toLowerCase())
    );
    const currentIds = new Set((currentValues || []).map((v) => v.Id));
    const toAdd = matched.filter((m) => !currentIds.has(m.Id));
    onChange([...(currentValues || []), ...toAdd]);
  };

  const handleClick = () => {
    const filters = {
      materialGroup: data.materialGroup.map((mg) => mg.Id),
      labeledFamily: data.labeledFamily.map((family) => family.Id),
      center: data.center.map((center) => center.Id),
      countries: data.countries.map((c) => c.Id),
      supplier: [],
      activeMaterial,
    };
    setIsLoading(true);
    onClick(filters).finally(() => setIsLoading(false));
  };

  return (
    <S.Container>
      <Loading isLoading={isLoading} fullHeightParent />
      <Feature>
        <S.RadioButtonContainer>
          <ClbRadioGroup orientation="line">
            <ClbRadioButton
              name="radio-01"
              value={"activeMaterial"}
              checked={activeMaterial === "activeMaterial"}
              onClbChange={handleActiveMaterial}
            >
              {intl.get("exportImportModal.masterData.onlyActiveMaterials")}
            </ClbRadioButton>
            <ClbRadioButton
              name="radio-02"
              value={"all"}
              checked={activeMaterial === "all"}
              onClbChange={handleActiveMaterial}
            >
              {intl.get("exportImportModal.masterData.allMaterials")}
            </ClbRadioButton>
          </ClbRadioGroup>
        </S.RadioButtonContainer>
      </Feature>
      <Feature>
        <div
          onPaste={(e) =>
            handleMultiPaste(e, data.countries, options.countries, "Descricao", (v) =>
              handleData(v, "countries")
            )
          }
        >
          <SelectBox
            required
            name="Country"
            label={intl.get("commons.country")}
            placeholder={"-"}
            valueKey="Id"
            labelKey="Descricao"
            value={data.countries}
            options={options.countries}
            onChange={(country) => handleData(country, "countries")}
            searchable
            openOnFocus
            clearable
            data-testid="country"
            multi
          />
        </div>
      </Feature>
      <Feature>
        <div
          onPaste={(e) =>
            handleMultiPaste(e, data.center, options.center, "Center", (v) =>
              handleData(v, "center")
            )
          }
        >
          <SelectBox
            required
            name="Center"
            label={intl.get("commons.destiny")}
            placeholder={"-"}
            valueKey="Id"
            labelKey="Description"
            value={data.center}
            options={options.center}
            onChange={(center) => handleData(center, "center")}
            searchable
            clearable
            multi
            data-testid="center"
          />
        </div>
      </Feature>
      <Feature>
        <div
          onPaste={(e) =>
            handleMultiPaste(e, data.materialGroup, options.materialGroup, "Description", (v) =>
              handleData(v, "materialGroup")
            )
          }
        >
          <SelectBox
            required
            name="MaterialGroup"
            label={intl.get("commons.materialGroup")}
            placeholder={"-"}
            valueKey="Id"
            labelKey="Description"
            value={data.materialGroup}
            options={options.materialGroup}
            onChange={(materialGroup) => handleData(materialGroup, "materialGroup")}
            searchable
            openOnFocus
            clearable
            data-testid="materialGroup"
            multi
          />
        </div>
      </Feature>
      <Feature>
        <div
          onPaste={(e) =>
            handleMultiPaste(e, data.labeledFamily, options.labeledFamily, "Description", (v) =>
              handleData(v, "labeledFamily")
            )
          }
        >
          <SelectBox
            required
            name="LabeledFamily"
            label={intl.get("commons.labeledFamily")}
            placeholder={"-"}
            valueKey="Id"
            labelKey="Description"
            value={data.labeledFamily}
            options={options.labeledFamily}
            onChange={(family) => handleData(family, "labeledFamily")}
            searchable
            openOnFocus
            clearable
            data-testid="labeledFamily"
            multi
          />
        </div>
      </Feature>
      <S.ButtonContainer>
        <Button
          buttonType="secondary"
          value={intl.get("exportImportModal.butttons.download")}
          onClick={handleClick}
          style={{ width: "120px" }}
        />
      </S.ButtonContainer>
    </S.Container>
  );
};

FilterDataUploadV2.propTypes = {
  onClick: PropTypes.func.isRequired,
};

export { FilterDataUploadV2 };
